import type FeatureLike from 'ol/Feature';
import { getCenter } from 'ol/extent';
import OlPoint from 'ol/geom/Point';
import type Polygon from 'ol/geom/Polygon';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import type { StyleFilter, StyleSymbolizer } from '../lib/layersApi';
import type { LayerConfig } from './demoLayers';

/** OL's Cluster source only works out of the box for Point sources — its default
 * geometryFunction asserts the geometry type is 'Point' and throws otherwise,
 * which is exactly what broke clustering for polygon (and line) layers: every
 * cluster refresh threw and the layer never rendered. Supplying this function
 * makes it accept any geometry type by reducing it to a representative point —
 * a polygon's interior point (guaranteed inside the shape, unlike an extent
 * center which can fall outside a concave/L-shaped polygon) or, for anything
 * else, its extent center. Also used (unclustered) to anchor an icon-mode
 * marker at one point instead of tracing every ring vertex of a polygon. */
export function clusterGeometryFunction(feature: FeatureLike): OlPoint | null {
  const geometry = feature.getGeometry();
  if (!geometry) return null;
  const type = geometry.getType();
  if (type === 'Point') return geometry as OlPoint;
  if (type === 'Polygon') {
    return (geometry as Polygon).getInteriorPoint();
  }
  return new OlPoint(getCenter(geometry.getExtent()));
}

function styleForLayer(color: string, selected: boolean) {
  const strokeColor = selected ? '#3b82f6' : color;
  return new Style({
    fill: new Fill({ color: selected ? `${color}55` : `${color}33` }),
    stroke: new Stroke({ color: strokeColor, width: selected ? 3 : 2 }),
    image: new CircleStyle({
      radius: selected ? 7 : 5,
      fill: new Fill({ color: selected ? `${color}cc` : color }),
      stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
    }),
  });
}

type MarkerConfig = { color: string; markerMode?: 'color' | 'icon'; markerIconUrl?: string };

/** Every uploaded icon gets cropped to the same fixed pixel size by the upload
 * editor, so one shared scale keeps every layer's icon markers the same size
 * on screen regardless of what image was uploaded. */
const ICON_MARKER_SCALE = 0.4;

/** Point/polygon marker style — plain colored circle (styleForLayer, the
 * default and the only option before this) or an uploaded icon. The selected
 * state's visual (bigger + highlighted) is preserved either way: for icons, a
 * tinted halo ring renders behind a slightly enlarged icon instead of the
 * bigger/darker circle color mode gets, since a Style with `image: Icon` can't
 * itself grow a colored ring the way CircleStyle's fill/stroke do. */
export function resolveMarkerStyle(cfg: MarkerConfig | undefined, selected: boolean): Style | Style[] {
  const color = cfg?.color ?? '#2563eb';
  if (cfg?.markerMode === 'icon' && cfg.markerIconUrl) {
    const icon = new Style({
      geometry: clusterGeometryFunction as unknown as string,
      image: new Icon({ src: cfg.markerIconUrl, scale: selected ? ICON_MARKER_SCALE * 1.3 : ICON_MARKER_SCALE }),
    });
    if (!selected) return icon;
    const halo = new Style({
      geometry: clusterGeometryFunction as unknown as string,
      image: new CircleStyle({
        radius: 22,
        fill: new Fill({ color: `${color}33` }),
        stroke: new Stroke({ color: '#3b82f6', width: 2 }),
      }),
    });
    return [halo, icon];
  }
  return styleForLayer(color, selected);
}

/** Loosely-typed comparison for style filter values — a categorized rule's
 * value is almost always a string (an attribute's raw value, e.g. "residential"),
 * a graduated/rule-based one's is almost always numeric. Comparing as numbers
 * whenever both sides parse as finite numbers covers "42" == 42 (JSON round-trip
 * through a shapefile .dbf/CSV import commonly stringifies numbers); otherwise
 * falls back to a plain string compare. */
function compareStyleValues(a: unknown, b: unknown): number {
  if (typeof a !== 'boolean' && typeof b !== 'boolean') {
    const na = Number(a);
    const nb = Number(b);
    if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  }
  const sa = String(a ?? '');
  const sb = String(b ?? '');
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

function evalStyleFilter(filter: StyleFilter | null | undefined, props: Record<string, unknown>): boolean {
  if (!filter) return true;
  if (filter.op === 'and') return filter.filters.every((f) => evalStyleFilter(f, props));
  if (filter.op === 'between') {
    const v = Number(props[filter.field]);
    const min = Number(filter.min);
    const max = Number(filter.max);
    return !Number.isNaN(v) && !Number.isNaN(min) && !Number.isNaN(max) && v >= min && v <= max;
  }
  const cmp = compareStyleValues(props[filter.field], filter.value);
  switch (filter.op) {
    case 'eq':
      return cmp === 0;
    case 'ne':
      return cmp !== 0;
    case 'lt':
      return cmp < 0;
    case 'le':
      return cmp <= 0;
    case 'gt':
      return cmp > 0;
    case 'ge':
      return cmp >= 0;
    default:
      return false;
  }
}

/** Object filtering (see MapContext's attributeFilters / LayersDrawer's "Фильтр
 * по полю") — a single field==value check, reusing the same loose comparison
 * as style rules so "42" (import round-trip) still matches 42. Kept separate
 * from evalStyleFilter/StyleFilter (which covers a whole imported SLD/QML rule
 * tree) since a user-picked map filter is always just one equality check. */
export function matchesAttributeValue(props: Record<string, unknown>, fieldCode: string, value: string): boolean {
  return compareStyleValues(props[fieldCode], value) === 0;
}

/** First rule (in order) whose filter matches wins; an isElse rule always
 * matches once reached — real exports always put it last, same as SLD/QGIS's
 * own evaluation order. Falls through to the imported style's fallback
 * symbolizer (SLD's unconditioned rule / QGIS's blank-value category) when
 * nothing matches, and to `null` (→ the flat color/opacity styling) when the
 * layer has no rule-based style at all. */
function matchStyleRule(layer: LayerConfig | undefined, feature: FeatureLike): StyleSymbolizer | null {
  if (layer?.styleType !== 'rule_based') return null;
  const props = feature.getProperties() as Record<string, unknown>;
  for (const rule of layer.styleRules ?? []) {
    if (rule.isElse || evalStyleFilter(rule.filter, props)) return rule.symbolizer;
  }
  return layer.styleFallback ?? null;
}

function hexAlphaSuffix(opacity: number): string {
  const a = Math.round(Math.min(1, Math.max(0, opacity)) * 255);
  return a.toString(16).padStart(2, '0');
}

/** Builds an OL Style from one imported rule's symbolizer — same visual
 * language as styleForLayer (fill+stroke+circle-image so it renders sensibly
 * regardless of the layer's actual geometry type), just reading per-rule
 * color/opacity/width instead of one flat color per layer. */
function styleFromSymbolizer(sym: StyleSymbolizer, selected: boolean): Style {
  const fillColor = sym.fill ?? sym.stroke ?? '#2563eb';
  const fillOpacity = sym.fillOpacity ?? 0.6;
  const strokeColor = selected ? '#3b82f6' : (sym.stroke ?? sym.fill ?? '#2563eb');
  const strokeWidth = sym.strokeWidth ?? (selected ? 3 : 2);
  const pointRadius = sym.pointRadius ?? (selected ? 7 : 5);
  return new Style({
    fill: new Fill({ color: `${fillColor}${hexAlphaSuffix(selected ? Math.min(1, fillOpacity + 0.2) : fillOpacity)}` }),
    stroke: new Stroke({ color: strokeColor, width: selected ? strokeWidth + 1 : strokeWidth }),
    image: new CircleStyle({
      radius: selected ? pointRadius + 2 : pointRadius,
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
    }),
  });
}

/** The one entry point every rendering/selection call site should use instead
 * of resolveMarkerStyle directly — matches the feature against the layer's
 * imported style rules (see matchStyleRule) first, and only falls back to the
 * flat single-style path (resolveMarkerStyle) when the layer has no
 * rule-based style or nothing matched and there's no fallback either. */
export function resolveLayerStyle(layer: LayerConfig | undefined, feature: FeatureLike, selected: boolean): Style | Style[] {
  const sym = matchStyleRule(layer, feature);
  if (sym) return styleFromSymbolizer(sym, selected);
  return resolveMarkerStyle(layer, selected);
}
